import { Component, Inject, OnInit } from '@angular/core';
import { FixedAsset } from '../../../../../../models/entities/accounting/fixed-asset';
import { Liability } from '../../../../../../models/entities/accounting/liability';
import { Equity } from '../../../../../../models/entities/accounting/equity';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ChartOfAccountsService } from '../../../../../../services/accounting/chart-of-accounts.service';
import { GenericDialogResult } from '../../../../shared/generic-mat-dialog/generic-dialog-result';
import { validateRecursive } from '../../../../../../directives/validators/validate-recursive';
import moment from 'moment-timezone';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogModule as MatDialogModule, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { SharedModule } from '../../../../../shared/shared.module';
import { CurrencyInputComponent } from '../../../../../shared/currency-input/currency-input.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { StringValidatorsModule } from '../../../../../../directives/validators/string/string-validators.module';
import { GrowlerService } from '../../../../../shared/growler/growler.service';
import { take } from 'rxjs/operators';
import { ControlMessagesComponent } from '../../../../../shared/control-messages/control-messages.component';

export interface AccountEditorDialogInput
{
    create: boolean;
    organizationId: number;
    account?: FixedAsset | Liability | Equity;
}

@Component({
    selector: 'app-account-editor-dialog',
    standalone: true,
    templateUrl: './account-editor-dialog.component.html',
    imports: [
        MatDialogModule,
        SharedModule,
        CurrencyInputComponent,
        MatDatepickerModule,
        MatLegacyCheckboxModule,
        StringValidatorsModule,
        ControlMessagesComponent,
    ],
})
export class AccountEditorDialogComponent implements OnInit
{

    public title = '';
    public contextNoun = '';
    public submitInFlight = false;

    public form: UntypedFormGroup = null;

    readonly accountsReceivableMessage = 'Starting balances for Accounts Receivable should be input by adding invoices to units.';
    readonly netIncomeMessage = 'Net Income is automatically calculated from the starting balances of your income and expense categories.';
    // SD-8209: Retained Earnings had no explanation at all on either surface.
    readonly retainedEarningsMessage = 'Retained Earnings is automatically calculated from prior-year activity, so it cannot have a starting balance.';

    constructor(
            private _chartOfAccountsService: ChartOfAccountsService,
            private _dialogRef: MatDialogRef<AccountEditorDialogComponent>,
            private _growler: GrowlerService,
            private _formBuilder: UntypedFormBuilder,
            @Inject(MAT_DIALOG_DATA) public data: AccountEditorDialogInput,
    ) {
    }

    get isAccountsReceivable(): boolean {
        return this.data.account instanceof FixedAsset &&
               this.data.account.internalUse === FixedAsset.INTERNAL_USE_ACCOUNTS_RECEIVABLE;
    }

    get isNetIncome(): boolean {
        return this.data.account instanceof Equity &&
               this.data.account.internalUse === Equity.INTERNAL_USE_NET_INCOME;
    }

    /**
     * SD-8209. Same Net-Income-only gap the v2 panel had: the API refuses a starting balance on
     * BOTH computed equity accounts, so Retained Earnings must be gated here too.
     */
    get isRetainedEarnings(): boolean {
        return this.data.account instanceof Equity &&
               this.data.account.internalUse === Equity.INTERNAL_USE_RETAINED_EARNINGS;
    }

    get isComputedEquity(): boolean {
        return this.isNetIncome || this.isRetainedEarnings;
    }

    get computedEquityMessage(): string {
        return this.isNetIncome ? this.netIncomeMessage : this.retainedEarningsMessage;
    }

    ngOnInit() {
        this.form = this._formBuilder.group({
            label: [
                this.data.account?.label || '',
                Validators.required,
            ],
            startingBalance: [
                this.data.account?.startingBalance || 0,
                Validators.required,
            ],
            asOf: [
                this.data.account?.startingBalanceDate || null,
                Validators.required,
            ],
        });
        if (this.isAccountsReceivable || this.isComputedEquity) {
            this.form.get('startingBalance').disable();
            this.form.get('asOf').disable();
        }

        if (this.data.account instanceof FixedAsset && !this.data.account.owner && !this.data.account.internalUse) {
            this.form.addControl('useLikeBankAccount', this._formBuilder.control(false));
        }

        switch (this.data.account.constructor) {
            case FixedAsset:
                this.contextNoun = 'Asset';
                break;
            case Liability:
                this.contextNoun = 'Liability';
                break;
            case Equity:
                this.contextNoun = 'Equity';
                break;
        }

        this.title = this.data.create ? `Add ${this.contextNoun}` : `Update ${this.contextNoun}`;

    }

    closeDialog() {
        this._dialogRef.close(<GenericDialogResult>{success: false});
    }

    save() {
        // The template already disables the button on submitInFlight, but that only stops a mouse —
        // a re-entrant call (double Enter, programmatic invoke) still got through, because save()
        // set the flag and never read it. Guard at entry so the request can't be fired twice.
        if (this.submitInFlight) {
            return;
        }

        validateRecursive(this.form);

        if (!this.form.valid) {
            // Shouldn't be able to trigger save() with an invalid form, but just in case.
            this._growler.error('Missing fields', 'Please double check all required fields.');
            return;
        }

        const requestBody = this.form.getRawValue();
        // getRawValue() INCLUDES disabled controls, so the disable() above never actually stopped a
        // starting balance being submitted for a computed equity account — only the server rejection
        // did. Send 0 so the request matches what the dialog tells the user. Mirrors the v2 panel.
        if (this.isComputedEquity) {
            requestBody.startingBalance = 0;
        }
        if (moment.isMoment(requestBody.asOf)) {
            requestBody.asOf = requestBody.asOf.format('YYYY-MM-DD');
        }

        if (this.data.create) {
            let request = null;
            switch (this.data.account.constructor) {
                case FixedAsset:
                    request = this._chartOfAccountsService.createAsset(this.data.organizationId, requestBody);
                    break;
                case Liability:
                    request = this._chartOfAccountsService.createLiability(this.data.organizationId, requestBody);
                    break;
                case Equity:
                    request = this._chartOfAccountsService.createEquity(this.data.organizationId, requestBody);
                    break;
            }
            if (request !== null) {
                this.submitInFlight = true;
                request.pipe(take(1)).subscribe(
                        () => {
                            this.submitInFlight = false;
                            this._growler.success('Success', this.contextNoun + ' added successfully');
                            this._dialogRef.close(<GenericDialogResult>{success: true});
                        },
                        (errorResponse) => {
                            this.submitInFlight = false;
                            this._growler.error(
                                    'Error',
                                    // The API nests under HttpErrorResponse.error as
                                    // {message, errors:[{msg}]}, so a top-level `errors` is always
                                    // undefined and every failure threw here instead of showing the
                                    // server's message. Matches the v2 panel and 19 other sites.
                                    errorResponse?.error?.message
                                        ?? errorResponse?.error?.errors?.[0]?.msg
                                        ?? `Unable to save ${this.contextNoun.toLowerCase()}.`,
                            );
                        },
                );
            }
        } else {
            let request = null;
            switch (this.data.account.constructor) {
                case FixedAsset:
                    request = this._chartOfAccountsService.updateAsset(null, this.data.account.id, requestBody);
                    break;
                case Liability:
                    request = this._chartOfAccountsService.updateLiability(null, this.data.account.id, requestBody);
                    break;
                case Equity:
                    request = this._chartOfAccountsService.updateEquity(null, this.data.account.id, requestBody);
                    break;
            }
            if (request !== null) {
                this.submitInFlight = true;
                request.pipe(take(1)).subscribe(
                        () => {
                            this.submitInFlight = false;
                            this._growler.success('Success', this.contextNoun + ' updated successfully');
                            this._dialogRef.close(<GenericDialogResult>{success: true});
                        },
                        (errorResponse) => {
                            this.submitInFlight = false;
                            this._growler.error(
                                    'Error',
                                    // The API nests under HttpErrorResponse.error as
                                    // {message, errors:[{msg}]}, so a top-level `errors` is always
                                    // undefined and every failure threw here instead of showing the
                                    // server's message. Matches the v2 panel and 19 other sites.
                                    errorResponse?.error?.message
                                        ?? errorResponse?.error?.errors?.[0]?.msg
                                        ?? `Unable to save ${this.contextNoun.toLowerCase()}.`,
                            );
                        },
                );
            }
        }
    }
}
